import React, { useState, useRef, useEffect, useCallback } from 'react';
import { getSpeakerColor } from '../../utils/speakerColors';
import { Speaker } from '../../types';
import { UserIcon } from '../../icons';

interface EditorUtteranceSpeakerProps {
  utteranceId: string;
  speakerId: string;
  speakers: Speaker[];
  onSpeakerChange: (utteranceId: string, speakerId: string) => void;
}

const EditorUtteranceSpeaker: React.FC<EditorUtteranceSpeakerProps> = ({
  utteranceId,
  speakerId,
  speakers,
  onSpeakerChange,
}) => {
  const currentSpeaker = speakers.find((s) => s.id === speakerId);
  const speakerName =
    currentSpeaker?.displayName || currentSpeaker?.name || 'Unknown';
  const [showSpeakerSelect, setShowSpeakerSelect] = useState(false);
  const speakerSelectRef = useRef<HTMLDivElement>(null);

  const handleSpeakerClick = () => {
    setShowSpeakerSelect(true);
  };

  const handleSpeakerChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newSpeakerId = e.target.value;
      if (newSpeakerId !== speakerId) {
        onSpeakerChange(utteranceId, newSpeakerId);
      }
      setShowSpeakerSelect(false);
    },
    [utteranceId, speakerId, onSpeakerChange]
  );

  const handleKeyDownGlobal = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showSpeakerSelect) {
        setShowSpeakerSelect(false);
      }
    },
    [showSpeakerSelect]
  );

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (
      speakerSelectRef.current &&
      !speakerSelectRef.current.contains(e.target as Node)
    ) {
      setShowSpeakerSelect(false);
    }
  }, []);

  useEffect(() => {
    if (showSpeakerSelect) {
      document.addEventListener('keydown', handleKeyDownGlobal);
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('keydown', handleKeyDownGlobal);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showSpeakerSelect, handleKeyDownGlobal, handleClickOutside]);

  return (
    <div className="flex items-center space-x-2">
      <UserIcon size={16} color={getSpeakerColor(speakerId, speakers)} filled />
      {showSpeakerSelect ? (
        <div ref={speakerSelectRef} className="relative">
          <select
            value={speakerId}
            onChange={handleSpeakerChange}
            className="font-semibold text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            autoFocus
          >
            {speakers.map((speaker) => (
              <option key={speaker.id} value={speaker.id}>
                {speaker.displayName}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <span
          data-tutorial="speaker-label"
          className="font-semibold text-gray-900 dark:text-gray-100 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded px-1 transition-colors"
          onClick={handleSpeakerClick}
          title="Click to change speaker"
        >
          {speakerName}
        </span>
      )}
    </div>
  );
};

export default EditorUtteranceSpeaker;
